import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, ArrowLeft } from "lucide-react";
import {
  WelcomeIllustration,
  DiscoverIllustration,
  JoinIllustration,
  HostIllustration,
  ConnectIllustration,
  ReadyIllustration,
} from "@/components/OnboardingIllustrations";

/**
 * OnboardingTour — first-time-user 6-step intro modal.
 *
 * Mobile-first: covers the viewport on small screens (rounded-t-3xl bottom
 * sheet style) and centres as a chunky card on desktop. Each step has its
 * own SVG illustration (lightweight, crisp on any DPI, zero network cost).
 *
 *   • Progress dots at the top.
 *   • Big chunky CTAs at the bottom (Skip + Next on early steps; Back/Next
 *     on middle steps; Skip + "Explore events" on the final step).
 *   • Triggered by AuthContext on first authenticated session whose profile
 *     has `has_completed_onboarding = false`.
 *   • On finish/skip the parent persists completion (DB or localStorage
 *     fallback) so the tour never auto-reopens.
 *
 * Animations are CSS-only (tailwindcss-animate) — keeps bundle tiny.
 */

const STEPS = [
  {
    eyebrow: "Welcome",
    title: "Welcome to inonit",
    body: "Find plans instantly, discover nearby events, and meet people around you.",
    Illustration: WelcomeIllustration,
  },
  {
    eyebrow: "Discover",
    title: "See what's happening nearby",
    body: "Browse events using categories, radius filters, and live local discovery.",
    Illustration: DiscoverIllustration,
  },
  {
    eyebrow: "Join",
    title: "Join plans in seconds",
    body: "Request to join public or private events and connect with people right next to you.",
    Illustration: JoinIllustration,
  },
  {
    eyebrow: "Host",
    title: "Post your own plans",
    body: "Football game, coffee run, workshop, movie night — create an event in seconds.",
    Illustration: HostIllustration,
  },
  {
    eyebrow: "Real-world",
    title: "Meet people naturally",
    body: "inonit surfaces real people and real experiences happening around you. (Nearby-at-event in beta.)",
    Illustration: ConnectIllustration,
  },
  {
    eyebrow: "Ready",
    title: "You're ready 🎉",
    body: "Your city is waiting.",
    Illustration: ReadyIllustration,
  },
];

export default function OnboardingTour({ open, onClose, onComplete }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  // Esc closes (treated as Skip).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;
  const { eyebrow, title, body, Illustration } = STEPS[step];

  const next = () => {
    if (isLast) {
      onComplete?.();
      navigate("/feed");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div
      data-testid="onboarding-tour"
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center sm:px-4 sm:py-8 bg-ink/55 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      <div
        className="w-full sm:max-w-md bg-paper border-[3px] border-ink rounded-t-3xl sm:rounded-3xl shadow-pop-lg overflow-hidden animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-300"
      >
        {/* Header: progress dots + Skip */}
        <div className="flex items-center justify-between px-5 py-4 border-b-[2.5px] border-ink">
          <div
            className="flex items-center gap-1.5"
            data-testid="onboarding-progress"
            aria-label={`Step ${step + 1} of ${STEPS.length}`}
          >
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={
                  "h-2 rounded-full transition-all duration-300 " +
                  (i === step
                    ? "w-6 bg-ink"
                    : i < step
                    ? "w-2 bg-ink/60"
                    : "w-2 bg-ink/15")
                }
              />
            ))}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="onboarding-skip"
            aria-label="Skip onboarding"
            className="text-[10px] uppercase tracking-[0.28em] font-extrabold text-ink/55 hover:text-ink transition-colors flex items-center gap-1.5"
          >
            Skip <X size={12} strokeWidth={3} />
          </button>
        </div>

        {/* Body */}
        <div
          key={step}
          className="px-6 pt-6 pb-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          <div
            className="bg-lime border-[2.5px] border-ink rounded-2xl aspect-[16/10] w-full flex items-center justify-center overflow-hidden shadow-pop-sm"
            data-testid={`onboarding-illustration-${step}`}
          >
            <Illustration />
          </div>

          <span className="block mt-5 text-[10px] uppercase tracking-[0.32em] font-extrabold text-ink/55">
            {eyebrow} · {step + 1}/{STEPS.length}
          </span>
          <h2
            id="onboarding-title"
            data-testid={`onboarding-step-${step}-title`}
            className="font-display text-3xl md:text-4xl font-bold leading-[1.02] tracking-tight mt-2"
          >
            {title}
          </h2>
          <p
            data-testid={`onboarding-step-${step}-body`}
            className="text-ink/70 mt-3 text-[15px] leading-relaxed"
          >
            {body}
          </p>
        </div>

        {/* Footer CTAs */}
        <div className="px-6 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:pb-6 flex items-center gap-3">
          {!isFirst && (
            <button
              type="button"
              onClick={back}
              data-testid="onboarding-back"
              aria-label="Previous step"
              className="shrink-0 w-12 h-12 rounded-full border-[2.5px] border-ink flex items-center justify-center hover:bg-ink hover:text-paper transition-colors"
            >
              <ArrowLeft size={18} strokeWidth={2.6} />
            </button>
          )}
          <button
            type="button"
            onClick={next}
            data-testid={isLast ? "onboarding-finish" : "onboarding-next"}
            className="flex-1 bg-ink text-paper border-[2.5px] border-ink rounded-full px-6 py-4 font-extrabold uppercase tracking-widest text-sm hover:bg-paper hover:text-ink transition-colors inline-flex items-center justify-center gap-2"
          >
            {isLast ? "Explore events" : "Next"}
            <ArrowRight size={16} strokeWidth={2.8} />
          </button>
        </div>
      </div>
    </div>
  );
}
